import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { SupabaseClient } from '@supabase/supabase-js';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';

const PAGE_SIZE = 50; // stays under the /api/admin/workers/batch 200-id cap

interface RecipientPageRow {
  id: string;
  conversations: { worker_id: number | null } | null;
}

interface RecipientMembershipRow {
  conversations: { worker_id: number | null } | null;
}

// Given a candidate set of worker ids (e.g. from a name/email search across
// ALL workers), returns the subset that actually received this broadcast —
// one indexed query, not N. Rides messages_broadcast_id_notnull_idx (the
// `metadata->>broadcast_id =` predicate) plus the conversations join.
export async function filterRecipientIds(
  client: SupabaseClient,
  broadcastId: string,
  workerIds: number[],
): Promise<number[]> {
  if (workerIds.length === 0) return [];
  try {
    const { data, error } = await client
      .from('messages')
      .select('conversations!inner(worker_id)')
      .eq('metadata->>broadcast_id', broadcastId)
      .in('conversations.worker_id', workerIds);
    if (error) throw error;
    const rows = (data ?? []) as unknown as RecipientMembershipRow[];
    const matched = new Set<number>();
    for (const row of rows) {
      const id = row.conversations?.worker_id;
      if (typeof id === 'number') matched.add(id);
    }
    // Dedupe while preserving the caller's ordering (search-result order)
    // for stable rendering, regardless of duplicate messages per worker.
    const seen = new Set<number>();
    const ordered: number[] = [];
    for (const id of workerIds) {
      if (matched.has(id) && !seen.has(id)) {
        seen.add(id);
        ordered.push(id);
      }
    }
    return ordered;
  } catch (err: unknown) {
    Sentry.captureException(err, {
      tags: { feature: 'comms', action: 'filter_recipient_ids' },
    });
    throw err;
  }
}

export function useBroadcastRecipients(broadcastId: string | null) {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [workerIds, setWorkerIds] = useState<number[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  // Bumped whenever broadcastId changes (and on unmount) so a response
  // from a stale broadcastId — either the initial page or a straggling
  // loadMore — can never clobber the state of whatever broadcastId is
  // current by the time the request resolves.
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (reset: boolean) => {
      if (!client || !ready || !broadcastId) return;
      const requestId = requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const offset = reset ? 0 : offsetRef.current;
        const {
          data,
          error: err,
          count,
        } = await client
          .from('messages')
          .select('id, conversations!inner(worker_id)', { count: 'exact' })
          .eq('metadata->>broadcast_id', broadcastId)
          .order('id', { ascending: true })
          .range(offset, offset + PAGE_SIZE - 1);
        if (requestIdRef.current !== requestId) return; // stale — superseded
        if (err) throw err;
        const rows = (data ?? []) as unknown as RecipientPageRow[];
        const ids = rows
          .map((r) => r.conversations?.worker_id)
          .filter((id): id is number => typeof id === 'number');
        offsetRef.current = offset + rows.length;
        setTotal(count ?? null);
        setHasMore(count != null && offset + rows.length < count);
        // Legacy-imported broadcasts can have multiple messages per
        // conversation sharing the same metadata.broadcast_id (body +
        // creator follow-ups), so a page of message rows can repeat a
        // worker_id already seen. Dedupe on every write so the recipient
        // list never renders duplicate keys/rows. total/hasMore stay
        // message-row-based — the range() cursor is keyed off message rows,
        // not unique workers.
        setWorkerIds((prev) =>
          Array.from(new Set(reset ? ids : [...prev, ...ids])),
        );
      } catch (err: unknown) {
        if (requestIdRef.current !== requestId) return; // stale — superseded
        Sentry.captureException(err, {
          tags: { feature: 'comms', action: 'fetch_broadcast_recipients' },
        });
        setError((err as Error)?.message ?? 'Failed to load recipients');
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [client, ready, broadcastId],
  );

  useEffect(() => {
    requestIdRef.current += 1;
    offsetRef.current = 0;
    setWorkerIds([]);
    setTotal(null);
    setHasMore(false);
    void fetchPage(true);
    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => fetchPage(false), [fetchPage]);

  return { workerIds, total, loading, error, hasMore, loadMore };
}
